import type { ReactElement } from 'react';
import type { EntityTypeDto, SaveEntityTypeRequestDto } from '@gr/core/content/entities/dto';
import { ErrorCode } from '@gr/core/errors';
import { FieldType } from '@gr/core/content/entities/fields';
import { useT } from '../../../../app/di';
import { FormAlert, FormSelectField, FormTextField, useServerForm } from '../../../../ui-kit/form/form';
import type { ServerOutcome } from '../../../../ui-kit/form/server-errors';
import { FieldsEditor } from '../FieldsEditor/FieldsEditor';
import { ENTITY_TYPE_FORM_FIELDS, entityTypeFormValues, toSaveRequest, type FieldsFormValues } from '../model/fields-form';
import styles from './EntityTypeForm.module.scss';

export interface EntityTypeFormProps {
  id: string;
  /** null — новый справочник. */
  type: EntityTypeDto | null;
  /** Другие справочники — цели полей-ссылок. */
  otherTypes: readonly EntityTypeDto[];
  onSubmit(input: SaveEntityTypeRequestDto): Promise<ServerOutcome>;
}

/** ENT-UC-1, ENT-UC-2: названия, поле-заголовок и поля справочника; отправка — кнопкой с form={id}. */
export function EntityTypeForm({ id, type, otherTypes, onSubmit }: EntityTypeFormProps): ReactElement {
  const { t } = useT();
  const { form, submit } = useServerForm<FieldsFormValues>({
    defaultValues: entityTypeFormValues(type, t('entities.types.defaultTitleLabel')),
    fields: ENTITY_TYPE_FORM_FIELDS,
    onSubmit: (values) => onSubmit(toSaveRequest(values)),
  });
  const { control, watch, formState } = form;
  const titleCandidates = watch('fields').filter((row) => row.type === FieldType.Text);
  const disabled = formState.isSubmitting;
  return (
    <form id={id} className="u-stack" onSubmit={submit} noValidate>
      <FormAlert form={form} />
      <div className={styles.names}>
        <FormTextField control={control} name="name" label={t('entities.types.name')} rules={{ required: ErrorCode.FieldRequired }} required disabled={disabled} />
        <FormTextField control={control} name="pluralName" label={t('entities.types.pluralName')} disabled={disabled} />
      </div>
      <FormSelectField
        control={control}
        name="titleRowId"
        label={t('entities.types.titleField')}
        hint={t('entities.types.titleFieldHint')}
        options={titleCandidates.map((row) => ({ value: row.rowId, label: row.label || row.key }))}
        disabled={disabled}
      />
      <h3>{t('entities.editor.fields')}</h3>
      <FieldsEditor form={form} entityTypes={otherTypes} affectedCount={type?.entityCount ?? 0} tracksTitle />
    </form>
  );
}
